import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse } from 'bootstrap';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { Menu, MenuProps } from '../Menu';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { MenuPages, VisibleSubLinks } from '@types';
import styles from './Navigation.module.css';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { GetAllCategoriesWithProductsSuccessResponse } from '@services/useGetAllCategoriesWithProducts';
import { SITE_URLS } from '@constants';
import { useGetAllBundles } from '@services/useGetAllBundles';
import { useGetAllCategoriesWithServices } from '@services/useGetAllCategoriesWithServices';
import { useGetCampaigns } from '@services/useGetCampaigns';
import { useGetAllServices } from '@services/useGetAllServices';
import { useGetAllProducts } from '@services/useGetAllProducts';
import { useGetAllCategoriesWithProducts } from '@services/useGetAllCategoriesWithProducts';
import { getDynamicMenuItemValue } from '@utils/translation';

interface CategoryItem {
    uuid: string;
    name: string;
    description?: string;
    image?: string;
    over_18?: number;
    children?: CategoryItem[];
    menuCategoryType: 'product' | 'service';
}

export interface NavigationProps {
    menuActivePage?: MenuPages;
}

export const Navigation: FC<NavigationProps> = ({ menuActivePage }) => {
    const pageSize = useMediaQuery();
    const { t } = useTranslation();
    const {
        allCategoriesWithProducts,
        isLoadingAllCategoriesWithProducts,
        allCategoriesWithServices,
        isLoadingAllCategoriesWithServices,
        allBundles,
        campaigns,
        allSettings,
        isEndpointExpired
    } = useData();
    const { request: requestAllCategoriesWithProducts } = useGetAllCategoriesWithProducts();
    const { request: requestAllCategoriesWithServices } = useGetAllCategoriesWithServices();
    const { request: requestAllBundles } = useGetAllBundles();
    const { request: requestCampaigns } = useGetCampaigns();

    const translations = {
        products: t('navigation-component.tabs.products'),
        services: t('navigation-component.tabs.services'),
        campaigns: t('navigation-component.tabs.campaigns'),
        hampers: t('navigation-component.tabs.hampers'),
        merchants: t('navigation-component.tabs.merchants'),
        contacts: t('navigation-component.tabs.contacts'),
        all: t('menu-component.titles-and-buttons.all'),
        buttonAriaLabel: t('navigation-component.button-aria-label')
    };

    const dynamicProductsLabel = String(getDynamicMenuItemValue(allSettings?.data, 1) || translations.products);
    const dynamicServicesLabel = String(getDynamicMenuItemValue(allSettings?.data, 2) || translations.services);
    const dynamicMerchantsLabel = String(getDynamicMenuItemValue(allSettings?.data, 3) || translations.merchants);

    const [sidebar, setSidebar] = useState<Collapse | null>(null);

    // main menu links to be shown.
    // when in desktop size, when clicking a navigation bar link, only the respective menu link should be displayed
    // when in mobile, all menu links are displayed
    const [mainLinksToShow, setMainLinksToShow] = useState<string[]>([]);
    // main link that is not collapsed
    const [activeItem, setActiveItem] = useState<string | null>(null);
    // subcategories being shown
    const [showSubcategoriesOf, setShowSubcategoriesOf] = useState<VisibleSubLinks>(null);
    const [offsetTop, setOffsetTop] = useState<number>(0);
    const [menuData, setMenuData] = useState<MenuProps['menuData']>([]);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allBundles && requestAllBundles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allBundles]);

    useEffect(() => {
        !campaigns && requestCampaigns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns]);

    const isAllCategoriesWithProductsExpired = isEndpointExpired('allCategoriesWithProducts');
    useEffect(() => {
        (!allCategoriesWithProducts || isAllCategoriesWithProductsExpired) && !isLoadingAllCategoriesWithProducts && requestAllCategoriesWithProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithProducts, isAllCategoriesWithProductsExpired]);

    const isAllCategoriesWithServicesExpired = isEndpointExpired('allCategoriesWithServices');
    useEffect(() => {
        (!allCategoriesWithServices || isAllCategoriesWithServicesExpired) && !isLoadingAllCategoriesWithServices && requestAllCategoriesWithServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithServices, isAllCategoriesWithServicesExpired]);

    const getProductCategories: () => GetAllCategoriesWithProductsSuccessResponse['data'] = useCallback(
        () => allCategoriesWithProducts?.data || [],
        [allCategoriesWithProducts]
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getAllCategories: () => any[] = useCallback(() => {
        const productCategories = (allCategoriesWithProducts?.data || []).map((category) => ({
            ...category,
            menuCategoryType: 'product',
        }));

        const serviceCategories = (allCategoriesWithServices?.data || []).map((category) => ({
            ...category,
            menuCategoryType: 'service',
        }));

        const combinedCategories = [...productCategories, ...serviceCategories];

        // Sort by name
        combinedCategories.sort((a, b) => {
            if ((a.name || '') < (b.name || '')) return -1;
            if ((a.name || '') > (b.name || '')) return 1;
            return 0;
        });

        return combinedCategories;
    }, [allCategoriesWithProducts, allCategoriesWithServices]);

    useEffect(() => {
        const menuData: MenuProps['menuData'] = [
            {
                id: MenuPages.Products,
                label: String(getDynamicMenuItemValue(allSettings?.data, 1) || translations.products),
                unCollapses: allCategoriesWithProducts?.data
                    ? [
                          { label: translations.all, url: 'products' },
                          ...getProductCategories().map((item) => {
                              return item.children
                                  ? {
                                        label: item.name || '',
                                        subLinks: item.children.map((child) => {
                                            return { label: child.name || '', url: `products/category/${child.uuid}` };
                                        }),
                                    }
                                  : { label: item.name || '', url: `products/category/${item.uuid}` };
                          }),
                      ]
                    : [{ label: translations.all, url: 'products' }],
            },
            {
                id: MenuPages.Services,
                label: String(getDynamicMenuItemValue(allSettings?.data, 2) || translations.services),
                unCollapses: allCategoriesWithServices?.data
                    ? [
                          { label: translations.all, url: 'services' },
                          ...allCategoriesWithServices.data.map((item) => {
                              return item.children
                                  ? {
                                        label: item.name || '',
                                        subLinks: item.children.map((child) => {
                                            return { label: child.name || '', url: `services?categories=${child.uuid}` };
                                        }),
                                    }
                                  : { label: item.name || '', url: `services?categories=${item.uuid}` };
                          }),
                      ]
                    : [{ label: translations.all, url: 'services' }],
            },
            ...(campaigns && campaigns.data && campaigns.data.length > 0
                ? [
                      {
                          id: MenuPages.Campaigns,
                          label: translations.campaigns,
                          linkTo: 'campaigns',
                      },
                  ]
                : []),
            ...(allBundles && allBundles.data && allBundles.data.length > 0
                ? [
                      {
                          id: MenuPages.PromoBaskets,
                          label: translations.hampers,
                          linkTo: 'baskets',
                      },
                  ]
                : []),
            {
                id: MenuPages.Merchants,
                label: String(getDynamicMenuItemValue(allSettings?.data, 3) || translations.merchants),
                linkTo: 'merchants',
            },
            {
                id: MenuPages.Contacts,
                label: String(getDynamicMenuItemValue(allSettings?.data, 4) || translations.contacts),
                linkTo: 'contacts',
            },
        ];

        setMenuData(menuData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings, allCategoriesWithProducts, allCategoriesWithServices, campaigns]);

    useEffect(() => {
        // destroy the element when the component unmounts
        return () => {
            try {
                sidebar?.dispose();
            } catch (e) {
                console.log('catch error disposing of sidebar');
            }
        };
    }, [sidebar]);

    const sidebarRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const collapseInstance = Collapse.getOrCreateInstance(node, { toggle: false });
            setSidebar(collapseInstance);
        }
    }, []);

    /**
     * Calculate distance from top the Menu should be in mobile.
     * So that the Header is visible even when the Menu is open.
     */
    const updateHeaderOffset = () => {
        const header = document.getElementsByTagName('header')?.[0] as HTMLDivElement | null;
        setOffsetTop(header?.clientHeight || 0);
    };

    /**
     * Calculate distance from top, when page changes breakpoint.
     */
    useEffect(() => {
        updateHeaderOffset();
    }, [pageSize]);

    const openSidebar: (activeItem?: string) => void = (activeItem) => {
        // reset
        setShowSubcategoriesOf(null);
        setActiveItem(null);
        updateHeaderOffset();

        sidebar?.show();

        // open one item, or open all
        if (activeItem) {
            setMainLinksToShow([activeItem]);
            setActiveItem(activeItem);
        } else {
            setMainLinksToShow(menuData.map((item) => item.id));
            setActiveItem(null);
        }
    };

    const closeSidebar = () => {
        try {
            sidebar?.hide();
        } catch (e) {
            console.log('catch error closing sidebar');
        }

        setActiveItem(null);

        // close menu items
        menuData.forEach((item) => {
            if (item.unCollapses) {
                const elemToHide = document.getElementById(`menu-accordion-${item.id}`);
                if (elemToHide) {
                    const collapseInstance = Collapse.getOrCreateInstance(elemToHide);
                    collapseInstance.hide();
                }
            }
        });
    };

    return (
        <>
            <nav className={`navbar p-0 ${styles['main-navigation']}`} data-testid="main-navigation">
                <Container classes="flex-nowrap pe-0 bg-light">
                    <>
                        <button
                            className={`d-md-none navbar-toggler navbar-open ps-0 pe-3 ms-0 ${styles['navbar-toggler']} ${styles['navbar-open']}`}
                            onClick={() => openSidebar()}
                            aria-label={translations.buttonAriaLabel}>
                            <i className="fa-light fa-bars" />
                        </button>

                        <div className="d-none d-md-block w-100 pe-lg-3">
                            <ul className="row d-flex flex-row flex-wrap navbar-nav">
                                {menuData.map((item) => (
                                    <li
                                        key={`link-${item.id}`}
                                        className={`nav-item ${styles['nav-item']} ${menuActivePage === item.id ? `active ${styles.active}` : ''}`}>
                                        {item.unCollapses ? (
                                            <button
                                                className={`nav-link ${styles['nav-link']}`}
                                                onClick={() => openSidebar(item.id)}>
                                                {item.label}
                                            </button>
                                        ) : (
                                            <Anchor
                                                href={item.linkTo || ''}
                                                mode={AnchorMode.none}
                                                classes={`nav-link ${styles['nav-link']}`}>
                                                {item.label}
                                            </Anchor>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <SearchForm
                            getAllCategories={getAllCategories}
                            searchProductsLabel={dynamicProductsLabel}
                            searchServicesLabel={dynamicServicesLabel}
                            searchMerchantsLabel={dynamicMerchantsLabel}
                        />
                    </>
                </Container>
                <EndColor />
            </nav>
            <Menu
                offsetTop={offsetTop}
                menuRef={sidebarRef}
                menuData={menuData}
                mainLinksToShow={mainLinksToShow}
                setMainLinksToShow={setMainLinksToShow}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                showSubcategoriesOf={showSubcategoriesOf}
                setShowSubcategoriesOf={setShowSubcategoriesOf}
                closeSidebar={closeSidebar}
            />
        </>
    );
};

interface SearchSuggestion {
    label: string;
    url: string;
    type: 'general' | 'category' | 'item';
    categoryType?: 'product' | 'service';
    itemType?: 'product' | 'service';
}

interface SearchFormProps {
    getAllCategories: () => CategoryItem[];
    searchProductsLabel: string;
    searchServicesLabel: string;
    searchMerchantsLabel: string;
}

const SearchForm: FC<SearchFormProps> = ({
    getAllCategories,
    searchProductsLabel,
    searchServicesLabel,
    searchMerchantsLabel,
}) => {
    const { useGrayscaleTheme } = useThemes();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const { request: requestProducts } = useGetAllProducts();
    const { request: requestServices } = useGetAllServices();

    const { allProducts, allServices, isLoadingAllProducts, isLoadingAllServices, isEndpointExpired } = useData();
    
    const prefetchSearchData = () => {
        if (!isLoadingAllProducts && (!allProducts || isEndpointExpired('allProducts'))) requestProducts();
        if (!isLoadingAllServices && (!allServices || isEndpointExpired('allServices'))) requestServices();
    };

    const translations = {
        article: t('navigation-component.search.results.in'),
        fullSearch: t('navigation-component.search.placeholder'),
        searchProducts: searchProductsLabel,
        searchServices: searchServicesLabel,
        searchMerchants: searchMerchantsLabel,
        categoryPrefix: t('navigation-component.search.category-prefix'),
    };

    const [search, setSearch] = useState<string>('');
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<SearchSuggestion[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const buildLink = (href: string) => {
        const target = (href || '').trim();

        if (/^https?:\/\//i.test(target)) return target;

        const currentLang = i18n.resolvedLanguage || i18n.language;
        const fallbackSetting = i18n.options?.fallbackLng;
        const fallbackLang = typeof fallbackSetting === 'string' ? fallbackSetting : currentLang;

        const needsPrefix = !!currentLang && currentLang !== fallbackLang;
        const normalized = target.replace(/^\/+/, '');

        const alreadyPrefixed = normalized.startsWith(`${currentLang}/`) || normalized === currentLang;
        const path = needsPrefix && !alreadyPrefixed ? `${currentLang}/${normalized}` : normalized;

        return `/${path}`;
    };

    useEffect(() => {
        const fetchAndFilterSuggestions = () => {
            if (search.trim() === '' || search.trim().length < 4) {
                setFilteredSuggestions([]);
                setShowSuggestions(false);
                return;
            }
            const categories = getAllCategories();
            const lowerCaseSearch = search.toLowerCase();

            const wordStartMatch = (text: string) => {
                const lower = text.toLowerCase();
                return lower.startsWith(lowerCaseSearch) || lower.includes(' ' + lowerCaseSearch);
            };

            const newSuggestions: SearchSuggestion[] = [];

            newSuggestions.push(
                {
                    label: translations.searchProducts,
                    url: SITE_URLS.productsSearch.replace(':searchQuery', encodeURIComponent(search)),
                    type: 'general',
                },
                {
                    label: translations.searchServices,
                    url: SITE_URLS.servicesSearch.replace(':searchQuery', encodeURIComponent(search)),
                    type: 'general',
                },
                {
                    label: translations.searchMerchants,
                    url: SITE_URLS.merchantsSearch.replace(':searchQuery', encodeURIComponent(search)),
                    type: 'general',
                }
            );

            categories.forEach((category) => {
                if (category.name && wordStartMatch(category.name)) {
                    let url = '';
                    if (category.menuCategoryType === 'product') {
                        url = `${SITE_URLS.products}?categories=${category.uuid}&text=${encodeURIComponent(search)}`;
                    } else if (category.menuCategoryType === 'service') {
                        url = `${SITE_URLS.services}?categories=${category.uuid}&text=${encodeURIComponent(search)}`;
                    }

                    newSuggestions.push({
                        label: category.name || '',
                        url: url,
                        type: 'category',
                        categoryType: category.menuCategoryType,
                    });
                }

                if (category.children && category.children.length > 0) {
                    category.children.forEach((child) => {
                        if (child.name && wordStartMatch(child.name)) {
                            let url = '';
                            if (category.menuCategoryType === 'product') {
                                url = `${SITE_URLS.products}?categories=${child.uuid}&text=${encodeURIComponent(search)}`;
                            } else if (category.menuCategoryType === 'service') {
                                url = `${SITE_URLS.services}?categories=${child.uuid}&text=${encodeURIComponent(search)}`;
                            }
                            newSuggestions.push({
                                label: `${category.name} > ${child.name}`,
                                url: url,
                                type: 'category',
                                categoryType: category.menuCategoryType,
                            });
                        }
                    });
                }
            });

            allProducts?.data?.forEach((product) => {
                const productAboutTextMatches = !!product.aboutText && wordStartMatch(product.aboutText);

                if (productAboutTextMatches) {
                    newSuggestions.push({
                        label: product.name || '',
                        url: `${SITE_URLS.productDetail.replace(':uuid', `${product.uuid}`)}`,
                        type: 'item',
                        itemType: 'product',
                    });
                }
            });

            allServices?.data?.forEach((service) => {
                const serviceAboutTextMatches = !!service.aboutText && wordStartMatch(service.aboutText);

                if (serviceAboutTextMatches) {
                    newSuggestions.push({
                        label: service.name || '',
                        url: `${SITE_URLS.merchantDetail.replace(':uuid', `${service.storeUuid}`)}?services`,
                        type: 'item',
                        itemType: 'service',
                    });
                }
            });

            setFilteredSuggestions(newSuggestions.slice(0, 15));
            setShowSuggestions(true);
            setActiveIndex(-1);
        };

        const handler = setTimeout(() => {
            fetchAndFilterSuggestions();
        }, 300);

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, getAllCategories, allProducts, allServices]);

    const handleSubmit = (e?: React.FormEvent) => {
        e && e.preventDefault();
        const trimmed = search.trim();
        if (!trimmed) return;

        const target =
            activeIndex >= 0 && filteredSuggestions[activeIndex]
                ? filteredSuggestions[activeIndex].url
                : SITE_URLS.productsSearch.replace(':searchQuery', encodeURIComponent(trimmed));

        navigate(buildLink(target));
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (!showSuggestions || filteredSuggestions.length === 0) {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % filteredSuggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setActiveIndex(-1);
        }
    };

    return (
        <Form
            onSubmit={handleSubmit}
            classes={
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                ` ${styles['global-search-form']} d-flex align-self-stretch flex-grow-1 flex-lg-grow-0 mb-0 pe-4 py-1 position-relative`
            }>
            <div className="d-flex px-4 py-3 align-items-center">
                <i className="fa-light fa-magnifying-glass" />
            </div>
            <div className="d-flex flex-grow-1">
                <input
                    id="full-search"
                    type="text"
                    placeholder={translations.fullSearch}
                    aria-label="Search"
                    value={search}
                    onChange={(elem) => { prefetchSearchData(); setSearch(elem.target.value); }}
                    onKeyDown={handleKeyDown}
                />
            </div>
            <div>
                <button
                    id={styles['full-search-clear-input']}
                    className="text-light"
                    style={{ display: showSuggestions ? 'flex' : 'none' }}
                    onClick={() => setSearch('')}>
                    <i className="fa-light fa-close" />
                </button>

                <button type="submit" className={styles['screen-reader-only']}>
                    {t('navigation-component.search.placeholder')}
                </button>
            </div>
            <div id={styles['full-search-suggestions-list']} style={{ display: showSuggestions ? 'block' : 'none' }}>
                {filteredSuggestions.map((item, index) => (
                    <div
                        key={`suggestion-${index}`}
                        className={index === activeIndex ? `active ${styles.active}` : ''}>
                        <Anchor href={item.url} mode={AnchorMode.none} classes="d-flex text-decoration-none text-dark">
                            <>
                                <i className="fa-light fa-magnifying-glass mt-1 me-3 pe-2" />
                                <Typography classes="mb-0">
                                    <>
                                        {item.type === 'category' && (
                                            <>
                                                <Typography
                                                    fontSize="16"
                                                    tagVariant={TypographyTagVariants.span}
                                                    classes={styles['full-search-target']}>
                                                    {translations.categoryPrefix}
                                                </Typography>
                                                <Typography
                                                    fontSize="16"
                                                    tagVariant={TypographyTagVariants.span}
                                                    classes={styles['full-search-target']}>
                                                    {item.categoryType === 'product'
                                                        ? ` ${translations.searchProducts}`
                                                        : item.categoryType === 'service'
                                                          ? ` ${translations.searchServices}`
                                                          : ` ${translations.searchMerchants}`}
                                                </Typography>
                                                <Typography
                                                    fontSize="16"
                                                    tagVariant={TypographyTagVariants.span}
                                                    classes={styles['full-search-target']}>
                                                    {`: `}
                                                </Typography>
                                            </>
                                        )}
                                        {item.type === 'item' && (
                                            <Typography
                                                fontSize="16"
                                                tagVariant={TypographyTagVariants.span}
                                                classes={styles['full-search-target']}>
                                                {item.itemType === 'product'
                                                    ? ` ${translations.searchProducts}`
                                                    : item.itemType === 'service'
                                                      ? ` ${translations.searchServices}`
                                                      : ''}
                                            </Typography>
                                        )}
                                        <Typography
                                            tagVariant={TypographyTagVariants.span}
                                            color="primary"
                                            fontSize="16"
                                            fontWeight="800">
                                            {item.label}
                                        </Typography>
                                        {item.type === 'general' && (
                                            <Typography
                                                fontSize="16"
                                                tagVariant={TypographyTagVariants.span}
                                                classes={styles['full-search-target']}>
                                                {` ${translations.article} ${search}`}
                                            </Typography>
                                        )}
                                    </>
                                </Typography>
                            </>
                        </Anchor>
                    </div>
                ))}
            </div>
        </Form>
    );
};

const EndColor: FC = () => {
    const { useGrayscaleTheme } = useThemes();

    return (
        <div
            className={
                // theme
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` d-none d-sm-block ${styles['end-color']}`
            }></div>
    );
};
